import { type CSSProperties, type ReactNode } from 'react'
import { useInViewport } from '#app/utils/motion.ts'
import { HeroScene, type SceneVenue } from './HeroScene.tsx'

/**
 * The night-time opening scene: the festival house with its door open, the
 * neighbourhood on the hill with one window lit per concert, and the
 * headline copy. Used by the live front page and by the gate pages.
 */
export function FestivalHero({
	notice,
	eyebrow,
	title,
	titleLines,
	dates,
	lede,
	status,
	actions,
	venues,
	variant = 'live',
}: {
	/** e.g. the admin preview note, shown above everything else */
	notice?: ReactNode
	eyebrow: ReactNode
	/** the heading as one string, for assistive tech */
	title: string
	/** the heading as it is drawn, one entry per line */
	titleLines: string[]
	dates?: ReactNode
	lede?: ReactNode
	status?: ReactNode
	actions?: ReactNode
	venues: SceneVenue[]
	variant?: 'live' | 'gate'
}) {
	const [ref, inView] = useInViewport<HTMLElement>('0px')
	let step = 0
	const next = () => ({ '--hk-step': step++ }) as CSSProperties

	return (
		<section
			ref={ref}
			className="hk-hero"
			data-variant={variant}
			aria-labelledby="hk-hero-title"
		>
			<HeroScene venues={venues} variant={variant} paused={!inView} />
			<div className="hk-hero-inner">
				<div className="hk-hero-copy">
					{notice ? (
						<p className="hk-notice" role="status">
							{notice}
						</p>
					) : null}
					<p className="hk-eyebrow hk-enter" style={next()}>
						{eyebrow}
					</p>
					<h1 id="hk-hero-title" className="hk-title">
						<span className="sr-only">{title}</span>
						<span aria-hidden="true">
							{titleLines.map((line, i) => (
								<span key={i} className="hk-title-line">
									<span style={{ '--hk-line': i } as CSSProperties}>
										{line}
									</span>
								</span>
							))}
						</span>
					</h1>
					{dates ? (
						<p className="hk-dates hk-enter" style={next()}>
							<span>{dates}</span>
							<svg
								className="hk-underline"
								viewBox="0 0 300 16"
								preserveAspectRatio="none"
								aria-hidden="true"
							>
								<path d="M3 11.5C58 5.2 121 3.6 176 5.6c43 1.6 82 4.3 121 7.4" />
							</svg>
						</p>
					) : null}
					{lede ? (
						<div className="hk-lede hk-enter" style={next()}>
							{lede}
						</div>
					) : null}
					{status ? (
						<div className="hk-enter" style={next()}>
							{status}
						</div>
					) : null}
					{actions ? (
						<div className="hk-actions hk-enter" style={next()}>
							{actions}
						</div>
					) : null}
				</div>
			</div>
		</section>
	)
}
